/**
 * Camera/Viewport controller for PIXI stage
 * Handles zoom (centered on mouse) and panning without breaking node interactions
 */

export class Camera {
  constructor(stage, viewportWidth, viewportHeight) {
    this.stage = stage;
    this.zoom = 1;
    this.minZoom = 0.1;
    this.maxZoom = 10;

    // Center the stage initially (graph is centered at 0,0 in world space)
    this.stage.position.x = viewportWidth / 2;
    this.stage.position.y = viewportHeight / 2;

    // Panning state
    this.isPanning = false;
    this.panStart = { x: 0, y: 0 };
    this.stageStart = { x: 0, y: 0 };

    // For smooth zooming
    this.targetZoom = 1;
    this.zoomSpeed = 0.1;
  }

  /**
   * Setup event listeners for zoom and pan
   * @param {HTMLElement} canvas - The canvas element
   */
  setupEventListeners(canvas) {
    // Mouse wheel for zoom
    canvas.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false });

    // Right-click or middle-click for panning
    canvas.addEventListener('pointerdown', (e) => this.handlePanStart(e));
    canvas.addEventListener('pointermove', (e) => this.handlePanMove(e));
    canvas.addEventListener('pointerup', (e) => this.handlePanEnd(e));
    canvas.addEventListener('pointercancel', (e) => this.handlePanEnd(e));

    // Prevent context menu on right-click
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  /**
   * Handle mouse wheel for zooming
   * Zooms toward the mouse cursor position
   */
  handleWheel(e) {
    e.preventDefault();

    // Get mouse position in canvas coordinates
    const rect = e.target.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate zoom change
    const delta = -e.deltaY;
    const zoomChange = delta > 0 ? 1.1 : 0.9;
    const newZoom = this.zoom * zoomChange;

    // Clamp zoom
    if (newZoom < this.minZoom || newZoom > this.maxZoom) {
      return;
    }

    // Get world position of mouse before zoom (using current zoom)
    const worldPos = this.screenToWorld(mouseX, mouseY);

    // Apply new zoom
    this.zoom = newZoom;
    this.stage.scale.set(this.zoom);

    // Adjust stage position so the world point stays under the mouse
    // Formula: stagePos = mouseScreen - worldPos * zoom
    this.stage.position.x = mouseX - worldPos.x * this.zoom;
    this.stage.position.y = mouseY - worldPos.y * this.zoom;
  }

  /**
   * Start panning (on right-click or middle-click)
   */
  handlePanStart(e) {
    // Right-click (button 2) or middle-click (button 1) for panning
    if (e.button === 2 || e.button === 1) {
      this.isPanning = true;
      this.panStart = { x: e.clientX, y: e.clientY };
      this.stageStart = {
        x: this.stage.position.x,
        y: this.stage.position.y
      };
      e.stopPropagation(); // Prevent node drag
    }
  }

  /**
   * Handle panning movement
   */
  handlePanMove(e) {
    if (!this.isPanning) return;

    const dx = e.clientX - this.panStart.x;
    const dy = e.clientY - this.panStart.y;

    this.stage.position.x = this.stageStart.x + dx;
    this.stage.position.y = this.stageStart.y + dy;

    e.stopPropagation(); // Prevent node drag
  }

  /**
   * End panning
   */
  handlePanEnd(e) {
    if (this.isPanning) {
      this.isPanning = false;
      e.stopPropagation();
    }
  }

  /**
   * Convert screen coordinates to world coordinates
   * @param {number} screenX - Screen X coordinate
   * @param {number} screenY - Screen Y coordinate
   * @returns {Object} World coordinates {x, y}
   */
  screenToWorld(screenX, screenY) {
    return {
      x: (screenX - this.stage.position.x) / this.zoom,
      y: (screenY - this.stage.position.y) / this.zoom
    };
  }

  /**
   * Convert world coordinates to screen coordinates
   * @param {number} worldX - World X coordinate
   * @param {number} worldY - World Y coordinate
   * @returns {Object} Screen coordinates {x, y}
   */
  worldToScreen(worldX, worldY) {
    return {
      x: worldX * this.zoom + this.stage.position.x,
      y: worldY * this.zoom + this.stage.position.y
    };
  }

  /**
   * Reset camera to default view
   */
  reset() {
    this.zoom = 1;
    this.stage.scale.set(1);
    this.stage.position.set(0, 0);
  }

  /**
   * Center the camera on a specific world position
   * @param {number} worldX - World X coordinate
   * @param {number} worldY - World Y coordinate
   * @param {number} targetZoom - Optional zoom level
   */
  centerOn(worldX, worldY, targetZoom = null) {
    const canvas = this.stage.canvas;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    if (targetZoom !== null) {
      this.zoom = Math.max(this.minZoom, Math.min(targetZoom, this.maxZoom));
      this.stage.scale.set(this.zoom);
    }

    this.stage.position.x = centerX - worldX * this.zoom;
    this.stage.position.y = centerY - worldY * this.zoom;
  }

  /**
   * Get current zoom level
   * @returns {number} Current zoom
   */
  getZoom() {
    return this.zoom;
  }

  /**
   * Check if currently panning
   * @returns {boolean} True if panning
   */
  isPanningActive() {
    return this.isPanning;
  }
}
